import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { OverlayModule, ConnectedPosition } from "@angular/cdk/overlay";

import { environment } from "../../../environments/environment";

export interface GloTimeRange {
  startTime: string;
  endTime: string;
}

@Component({
  selector: "glo-timepicker",
  standalone: true,
  imports: [CommonModule, FormsModule, OverlayModule],
  templateUrl: "./glo-timepicker.component.html",
  styleUrl: "./glo-timepicker.component.scss",
})
export class GloTimepickerComponent implements OnInit, OnChanges {
  @Input() startTime = "09:00";
  @Input() endTime = "12:00";

  @Output() timeChange = new EventEmitter<GloTimeRange>();

  assetUrl = environment.assetBaseUrl;

  openMenu: "start" | "end" | null = null;

  timeOptions: string[] = this.generateTimeOptions(30);

  overlayPositions: ConnectedPosition[] = [
    {
      originX: "start",
      originY: "bottom",
      overlayX: "start",
      overlayY: "top",
      offsetY: 8,
    },
    {
      originX: "start",
      originY: "top",
      overlayX: "start",
      overlayY: "bottom",
      offsetY: -8,
    },
  ];

  ngOnInit(): void {
    this.normalizeRange();
    this.emitTimeRange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["startTime"] || changes["endTime"]) {
      this.normalizeRange();
    }
  }

  toggleMenu(menu: "start" | "end"): void {
    this.openMenu = this.openMenu === menu ? null : menu;
  }

  closeMenus(): void {
    this.openMenu = null;
  }

  selectTime(type: "start" | "end", time: string): void {
    if (this.isTimeOptionDisabled(type, time)) {
      return;
    }

    if (type === "start") {
      this.startTime = time;

      if (
        this.timeToMinutes(this.endTime) <= this.timeToMinutes(this.startTime)
      ) {
        this.endTime = this.addMinutes(this.startTime, 30);
      }
    } else {
      this.endTime = time;
    }

    this.emitTimeRange();
    this.closeMenus();
  }

  isTimeOptionDisabled(type: "start" | "end", time: string): boolean {
    if (type !== "end") {
      return false;
    }

    return this.timeToMinutes(time) <= this.timeToMinutes(this.startTime);
  }

  formatDisplayTime(time: string): string {
    const [hourStr, minute] = time.split(":");

    let hour = Number(hourStr);

    const suffix = hour >= 12 ? "PM" : "AM";

    hour = hour % 12;

    if (hour === 0) {
      hour = 12;
    }

    return `${hour}:${minute} ${suffix}`;
  }

  private normalizeRange(): void {
    if (!this.isValidTime(this.startTime)) {
      this.startTime = "09:00";
    }

    if (!this.isValidTime(this.endTime)) {
      this.endTime = "12:00";
    }

    if (
      this.timeToMinutes(this.endTime) <= this.timeToMinutes(this.startTime)
    ) {
      this.endTime = this.addMinutes(this.startTime, 30);
    }
  }

  private emitTimeRange(): void {
    this.timeChange.emit({
      startTime: this.startTime,
      endTime: this.endTime,
    });
  }

  private isValidTime(time: string): boolean {
    return /^([01]\d|2[0-3]):[0-5]\d$/.test(time || "");
  }

  private timeToMinutes(time: string): number {
    const [hour, minute] = time.split(":").map(Number);

    return hour * 60 + minute;
  }

  private addMinutes(time: string, minutesToAdd: number): string {
    const total = Math.min(
      this.timeToMinutes(time) + minutesToAdd,
      23 * 60 + 30,
    );

    const hour = Math.floor(total / 60);
    const minute = total % 60;

    return `${String(hour).padStart(2, "0")}:${String(minute).padStart(
      2,
      "0",
    )}`;
  }

  private generateTimeOptions(intervalMinutes: number): string[] {
    const times: string[] = [];

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += intervalMinutes) {
        const hh = String(hour).padStart(2, "0");

        const mm = String(minute).padStart(2, "0");

        times.push(`${hh}:${mm}`);
      }
    }

    return times;
  }
}
